import { Card as MUICard, CardActionArea, CardContent, Typography, Box, useTheme } from '@mui/material';
import { Activity, Occasion, PlayerData } from '@oskarjiang/matchi-types';
import { useQuery } from '@tanstack/react-query';
import { baseUrl } from '../../constants/endpoints';

type Props = {
	activity: Activity;
	occasion: Occasion;
	loading: boolean;
	handleClick: Function;
};

const timeForDateString = (dateString: string) => {
	const date = new Date(dateString);
	const hour = date.getHours().toString().padStart(2, '0');
	const minute = date.getMinutes().toString().padStart(2, '0');

	return `${hour}:${minute}`;
};

export const Card = ({ activity, occasion, handleClick }: Props) => {
	const theme = useTheme();

	// Fetch player data to determine valid/invalid players
	const { data: playerData } = useQuery<PlayerData[]>({
		queryKey: ['player-data'],
		queryFn: () => fetch(`${baseUrl}/playerData`).then((res) => res.json()),
		staleTime: 7 * 24 * 60 * 60 * 1000, // 1 week - rarely changes
	});

	// Calculate how many players have valid average levels
	const countValidPlayers = () => {
		if (!playerData || !occasion.occasionParticipants || occasion.occasionParticipants.length === 0) {
			return occasion.participants; // Default to all valid if no data
		}

		const validPlayers = occasion.occasionParticipants.filter((participant) =>
			playerData.some(
				(player) => player.player.userId === participant.userId && player.averageLevel !== null && player.averageLevel !== undefined
			)
		);

		return validPlayers.length;
	};

	const validPlayersCount = countValidPlayers();
	const invalidPlayersCount = occasion.participants - validPlayersCount;
	const validPlayersRatio = validPlayersCount / occasion.maxNumParticipants;
	const invalidPlayersRatio = invalidPlayersCount / occasion.maxNumParticipants;

	// Colors for the progress bars - use only valid players for color decision
	const getValidPlayerColor = () => {
		// Calculate ratio based only on valid players
		const validOnlyRatio = validPlayersCount / occasion.maxNumParticipants;

		if (validOnlyRatio < 0.5) return theme.palette.success.main;
		if (validOnlyRatio < 0.8) return theme.palette.warning.main;
		return theme.palette.error.main;
	};

	return (
		<MUICard
			elevation={2}
			sx={{
				backgroundColor: occasion.isBookable ? theme.palette.background.paper : theme.palette.action.disabledBackground,
				margin: '8px',
				transition: 'transform 0.2s, box-shadow 0.2s',
				'&:hover': {
					transform: 'translateY(-3px)',
					boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
				},
			}}
		>
			<CardActionArea
				onClick={() => {
					handleClick();
				}}
			>
				<CardContent>
					<Typography>{activity.name}</Typography>
					<Typography variant="body2" color="text.secondary">
						{`${timeForDateString(occasion.startTime)} - ${timeForDateString(occasion.endTime)}`}
					</Typography>{' '}
					<Box sx={{ mt: 1, position: 'relative' }}>
						{/* Progress bar container */}
						<Box sx={{ position: 'relative', height: 6, borderRadius: 3, bgcolor: theme.palette.grey[200], overflow: 'hidden' }}>
							{/* Valid players bar */}
							<Box
								sx={{
									position: 'absolute',
									left: 0,
									top: 0,
									height: '100%',
									width: `${validPlayersRatio * 100}%`,
									bgcolor: getValidPlayerColor(),
									transition: 'width 0.3s ease',
								}}
								title="Valid players"
							/>{' '}
							{/* Invalid players bar */}
							<Box
								sx={{
									position: 'absolute',
									left: `${validPlayersRatio * 100}%`,
									top: 0,
									height: '100%',
									width: `${invalidPlayersRatio * 100}%`,
									bgcolor: 'rgba(180, 180, 180, 0.8)',
									transition: 'width 0.3s ease, left 0.3s ease',
								}}
								title="Invalid players (missing average level)"
							/>
						</Box>
						{/* Participants count overlay - at the tip of the progress bar */}
						<Box
							sx={{
								position: 'absolute',
								left: `${(validPlayersRatio) * 100}%`,
								top: '50%',
								transform: 'translate(-50%, -50%)',
								bgcolor: 'rgba(255, 255, 255, 0.9)',
								px: 0.5,
								borderRadius: 0.5,
								fontSize: '0.75rem',
								fontWeight: 1200,
								color: 'text.primary',
								lineHeight: 1,
								backdropFilter: 'blur(2px)',
								zIndex: 10,
								border: '1px solid rgba(0, 0, 0, 0.1)',
								transition: 'left 0.3s ease',
							}}
						>
							{validPlayersCount}/{occasion.maxNumParticipants}
						</Box>
					</Box>
				</CardContent>
			</CardActionArea>
		</MUICard>
	);
};
